// src/cockpit4/workspaces/components/HermesDiscussCard.tsx
// ============================================
// Hermes 参谋 · 就地讨论卡（纯展示组件）
//
// 定位：从 MarketingStudioWorkspace 拆分出的展示层——蓝色就地讨论卡：
// 右上角「复制」/「放大」按钮 + 最近 3 条消息列表 + 输入框 + 「发送」+
// 「采纳」（仅存在 assistant 消息时显示）。
// 不持有任何状态/业务逻辑：全部由父组件经 props + 回调注入。
//
// 复制按钮逻辑原样搬迁自父组件：取最近一条 assistant 消息复制到剪贴板，
// 并把 #hermes-copy-btn 文字改为 "✅ 已复制"，1.5s 后清空。
// ============================================

import { Copy, Maximize2, Send, Save, Loader2 } from "lucide-react";

interface HermesDiscussCardProps {
  /** 对话消息列表（role: 用户/Hermes；content: 正文） */
  msgs: { role: "user" | "assistant"; content: string }[];
  /** textarea 输入值 */
  input: string;
  /** 输入变化回调 */
  onInputChange: (v: string) => void;
  /** 是否正在请求（发送按钮显示 Loader2 旋转并禁用） */
  busy: boolean;
  /** 点击「发送」 */
  onSend: () => void;
  /** 点击「采纳」：把 Hermes 最新建议写回周度大纲文件 */
  onApply: () => void;
  /** 点击「放大」：弹窗大屏预览完整对话 */
  onPreview: () => void;
}

export default function HermesDiscussCard({
  msgs,
  input,
  onInputChange,
  busy,
  onSend,
  onApply,
  onPreview,
}: HermesDiscussCardProps) {
  const hasAssistant = msgs.some((m) => m.role === "assistant");
  return (
    <div className="mt-2 rounded border border-blue-500/20 bg-blue-500/[0.04] backdrop-blur-md p-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-mono text-blue-300">Hermes 参谋 · 就地讨论</span>
        <div className="flex items-center gap-1">
          {/* v6.2.8 复制按钮：一键复制 Hermes 最新回答（采纳内容），不用手动选中 */}
          <button
            onClick={() => {
              const last = [...msgs].reverse().find((m) => m.role === "assistant");
              if (last) { navigator.clipboard?.writeText(last.content).then(() => {
                const btn = document.getElementById("hermes-copy-btn");
                if (btn) { btn.textContent = "✅ 已复制"; setTimeout(() => { btn.textContent = ""; }, 1500); }
              }).catch(() => {}); }
            }}
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
            title="复制 Hermes 最新回答"
          >
            <Copy size={9} />
            <span id="hermes-copy-btn"></span>
          </button>
          {/* v6.2.8 放大预览按钮：弹窗大屏查看完整对话（字体小/滚动不便时用） */}
          <button
            onClick={onPreview}
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
            title="放大预览完整对话"
          >
            <Maximize2 size={9} />
            放大
          </button>
          <span className="text-[8px] font-mono text-gray-600">经 Obsidian 检索/存档</span>
        </div>
      </div>
      {msgs.length > 0 && (
        <div className="mb-1 space-y-1 max-h-[110px] overflow-y-auto">
          {msgs.slice(-3).map((m, i) => (
            <div key={i} className={`rounded px-2 py-1 text-[10px] font-mono leading-relaxed ${m.role === "user" ? "bg-blue-500/10 text-blue-200" : "bg-white/[0.03] text-gray-300"}`}>
              <span className="opacity-60">{m.role === "user" ? "你" : "Hermes"}：</span>
              <span className="whitespace-pre-wrap break-words">{m.content}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-start gap-1.5">
        <textarea
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
          }}
          rows={2}
          placeholder='如："调整主题顺序" / "第 3 天换个主题"（Enter 发送，Shift+Enter 换行）'
          className="flex-1 px-2 py-1.5 text-[11px] font-mono bg-white/[0.03] border border-white/[0.08] rounded focus:outline-none focus:border-blue-500/40 placeholder-gray-600 text-gray-200 resize-y"
        />
        <button
          onClick={onSend}
          disabled={busy || !input.trim()}
          className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-mono text-white bg-blue-600/80 hover:bg-blue-600 rounded transition-colors disabled:opacity-40"
        >
          {busy ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
          发送
        </button>
        {hasAssistant && (
          <button
            onClick={onApply}
            className="flex items-center gap-1 px-2 py-1.5 text-[10px] font-mono text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded transition-colors"
            title="把 Hermes 最新建议写回周度大纲文件（经 Obsidian），概要自动刷新"
          >
            <Save size={10} />
            采纳
          </button>
        )}
      </div>
    </div>
  );
}
